<template>
  <Teleport to="body">
    <div v-if="isOpen" class="modal-backdrop" @click="handleBackdropClick">
      <div class="modal-dialog" @click.stop>
        <div class="modal-content">
          <div class="modal-controls">
            <div class="tab-buttons">
              <button
                class="tab-btn"
                :class="{ active: activeTab === 'app' }"
                @click="activeTab = 'app'"
              >
                {{ $t('download_modal.send_to_phone') }}
              </button>
              <button
                class="tab-btn"
                :class="{ active: activeTab === 'export' }"
                @click="activeTab = 'export'"
              >
                {{ $t('download_modal.export_file') }}
              </button>
              <button
                class="tab-btn"
                :class="{ active: activeTab === 'gps' }"
                @click="activeTab = 'gps'"
              >
                {{ $t('shared.send_to_garmin') }}
              </button>
            </div>
            <button type="button" class="close-btn" @click="closeModal" :aria-label="$t('shared.close')">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <DownloadModalSendtoPhoneTab
              v-if="activeTab === 'app'"
              :userData="userData"
              @close="closeModal"
            />

            <DownloadModalExportFileTab
              v-if="activeTab === 'export'"
              :userData="userData"
              @close="closeModal"
            />

            <DownloadModalSendToDeviceTab
              v-if="activeTab === 'gps'"
              :userData="userData"
              @close="closeModal"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import emitter from '../../utils/bus';
import DownloadModalSendtoPhoneTab from './DownloadModalSendtoPhoneTab.vue';
import DownloadModalExportFileTab from './DownloadModalExportFileTab.vue';
import DownloadModalSendToDeviceTab from './DownloadModalSendToDeviceTab.vue';

const isOpen = ref(false);
const activeTab = ref('app');
const isDownloading = ref(false); // Added missing ref

// Reactive user data
const userData = ref(window.user || null); // Added fallback to empty object

// Handle user updates
const handleUserUpdate = () => {
  userData.value = window.user; // Use spread to ensure reactivity
};

const openModal = (tab: 'app' | 'export' | 'gps' = 'app') => {
  isOpen.value = true;
  activeTab.value = tab;
  document.body.style.overflow = 'hidden';

  // Ensure we have the latest user data when opening
  handleUserUpdate();
};

const closeModal = () => {
  isOpen.value = false;
  document.body.style.overflow = '';
};

const handleBackdropClick = () => {
  closeModal();
};

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value && !isDownloading.value) {
    closeModal();
  }
};

// Method to update downloading state (can be called from child components)
const setDownloading = (state: boolean) => {
  isDownloading.value = state;
};

// Expose method for child components if needed
defineExpose({
  setDownloading
});

onMounted(() => {
  document.addEventListener('keydown', handleEscapeKey);
  // Expose open method globally for external use
  (window as any).openDownloadModal = openModal;
  emitter.on('user:updated', handleUserUpdate);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey);
  delete (window as any).openDownloadModal;
  // Remove user update listener - FIXED: using 'user:update'
  emitter.off('user:updated', handleUserUpdate);
});
</script>

<style scoped lang="scss">
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-dialog {
  max-width: 870px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content {
  border-radius: 12px;
  color: white;
  position: relative;
  background-color: var(--bs-body-bg); /* Assuming a dark background for the modal content */
  padding: 1em;
  height: 550px;
}

/* New styles for modal controls */
.modal-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1em 1em 0 1em; /* Adjust padding as needed */
  border-bottom: none; /* Remove the bottom border from the old header */
}

.tab-buttons {
  display: flex;
  gap: 4px;
  border-radius: 100px;
  background-color: var(--gray-dark);
  padding: .5em;
  /* Remove margin and width: fit-content from here */
}

.tab-btn {
  background-color: var(--gray-dark);
  color: rgba(255, 255, 255, 0.7);
  border: none;
  padding: 8px 24px;
  border-radius: 24px;
  /* font-size: 12px; */
  cursor: pointer;
  transition: all 0.2s;

  &.active {
    background-color: #505259;
    color: #ffffff;
    font-weight: 600;
  }
}

.close-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
}

.modal-body {
  padding: 0;
}

// Responsive Design
@media (max-width: 768px) {
  .modal-dialog {
    width: 100%;
    max-width: none;
    height: 100%;
    max-height: none;
  }

  .modal-content {
    height: 100%;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  .modal-controls {
    /* No longer flex-direction: column here to keep close-btn and tabs on the same "row" conceptually for positioning */
    display: block; /* Change to block to allow close-btn to be absolute within it */
    padding: 1em; /* Adjust padding for mobile */
    position: relative; /* For absolute positioning of close button */
  }

  .tab-buttons {
    width: 100%; /* Full width tabs */
    justify-content: space-around; /* Distribute space evenly */
    margin-top: 3em; /* Add top margin to push tabs down below the close button */
    /* Remove margin-bottom as it's not needed with display: block for modal-controls */
  }

  .tab-btn {
    flex: 1; /* Make tabs take equal width */
    text-align: center;
    padding: 8px;
    font-size: 12px !important;
  }

  .close-btn {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
  }

  .tab-buttons {
    flex-wrap: wrap;
  }
}
</style>
